import React, { useState } from 'react';
import SHA256 from 'crypto-js/sha256';
import MD5 from 'crypto-js/md5';
import SHA3 from 'crypto-js/sha3';

const algorithmInfo = {
  'SHA-256': {
    name: 'SHA-256',
    networks: ['Bitcoin', 'Bitcoin Cash'],
    generate: (input) => SHA256(input).toString(),
    description: 'Salida de 256 bits en hexadecimal. Usado por Bitcoin y Bitcoin Cash.',
    header: `SHA-256 es un algoritmo de hash criptográfico utilizado ampliamente en blockchain. Convierte cualquier conjunto de datos en una cadena única de 256 bits. Es determinista, rápido, y resistente a colisiones, lo que garantiza la integridad de la información.`
  },
  Ethash: {
    name: 'Ethash',
    networks: ['Ethereum (antes del merge)', 'Linea'],
    generate: (input) => 'Ethash: ' + SHA256(input).toString().substr(0, 64),
    description: 'Simulación de Ethash, basado en Keccak-256 en su base, usado en Ethereum y Linea.',
    header: `Ethash fue el algoritmo de consenso de Ethereum antes del paso a Proof-of-Stake. Está basado en Keccak y diseñado para ser resistente a la minería con ASIC, favoreciendo el uso de GPU.`
  },
  Scrypt: {
    name: 'Scrypt',
    networks: ['Litecoin'],
    generate: (input) => 'Scrypt: ' + MD5(input).toString(),
    description: 'Simulación de Scrypt, conocido por su mayor consumo de memoria, utilizado en Litecoin.',
    header: `Scrypt es un algoritmo de hash que consume más memoria que SHA-256, lo que lo hace más difícil de minar con hardware especializado. Litecoin lo adoptó para democratizar la minería.`
  },
  RandomX: {
    name: 'RandomX',
    networks: ['Monero'],
    generate: (input) => {
      const reversed = input.split('').reverse().join('');
      return 'RandomX: ' + SHA256(reversed).toString();
    },
    description: 'Simulación de RandomX, optimizado para CPU en minería de Monero.',
    header: `RandomX es un algoritmo diseñado para ejecutarse eficientemente en CPUs comunes, haciendo que la minería de Monero sea accesible y resistente a ASICs.`
  },
  'Keccak-256': {
    name: 'Keccak-256',
    networks: ['Ethereum (para firmas y direcciones)', 'Linea'],
    generate: (input) => 'Keccak-256: ' + SHA3(input, { outputLength: 256 }).toString(),
    description: 'Simulación de Keccak-256, utilizado en Ethereum para direcciones y firmas.',
    header: `Keccak-256 es la base de SHA-3, y se utiliza en Ethereum para generar direcciones, firmar transacciones y validar contratos.`
  },
  PolygonPoS: {
    name: 'PolygonPoS',
    networks: ['Polygon (Matic)'],
    generate: (input) => 'PolygonPoS: ' + SHA256(input).toString().substr(0, 64),
    description: 'Simulación para la cadena Proof-of-Stake de Polygon.',
    header: `Polygon PoS es una solución de escalado para Ethereum. Utiliza una combinación de algoritmos para validar bloques rápidamente y con bajas comisiones.`
  },
  DummyHash: {
    name: 'DummyHash',
    networks: ['Binance Smart Chain', 'Avalanche', 'Solana', 'Cardano', 'Ripple', 'Tron', 'EOS', 'Tezos'],
    generate: (input) => 'Dummy: ' + MD5(input).toString(),
    description: 'Simulación de un algoritmo genérico para múltiples redes blockchain.',
    header: `Este es un algoritmo ficticio para simular el uso de hash en otras cadenas como Solana, Cardano o BSC. Permite entender cómo se procesan datos aunque usen tecnologías distintas.`
  },
};

const BlockchainSimulator = () => {
  const [input, setInput] = useState('');
  const [selectedAlg, setSelectedAlg] = useState('SHA-256');
  const [hash, setHash] = useState('');

  const generateHash = () => {
    const algorithm = algorithmInfo[selectedAlg];
    const result = algorithm.generate(input);
    setHash(result);
  };

  const containerStyle = {
    width: '80%',
    margin: '40px auto',
    padding: '2rem',
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    borderRadius: '10px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    fontFamily: 'Segoe UI, sans-serif',
    marginTop:'5%'
  };

  const headerStyle = {
    marginBottom: '2rem',
    padding: '1.5rem',
    backgroundColor: '#f4f9f9',
    borderLeft: '5px solid #4CAF50',
    fontSize: '1.15rem',
    lineHeight: '1.6',
    color: '#333',
    textAlign: 'left'
  };

  const titleStyle = {
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: '1rem',
    textAlign: 'center',
    color: '#222'
  };

  const labelStyle = {
    fontSize: '1.2rem',
    fontWeight: '600',
    marginBottom: '0.5rem',
    color: '#444',
    display: 'block',
    textAlign: 'left'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.8rem',
    borderRadius: '6px',
    border: '1px solid #ccc',
    fontSize: '1rem',
    marginBottom: '1.5rem'
  };

  const buttonStyle = {
    padding: '0.8rem 1.5rem',
    backgroundColor: '#4CAF50',
    color: '#fff',
    fontSize: '1.1rem',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'block',
    margin: 'auto'
  };

  const resultStyle = {
    fontSize: '1rem',
    backgroundColor: '#f0f0f0',
    padding: '1rem',
    borderRadius: '6px',
    marginTop: '1.5rem',
    wordBreak: 'break-all',
    textAlign: 'left'
  };

  return (
    <div style={containerStyle}>
      <h2 style={titleStyle}>Simulación de Algoritmos de Hash</h2>

      <div style={headerStyle}>
        {algorithmInfo[selectedAlg].header}
      </div>

      <label htmlFor="algorithmSelect" style={labelStyle}>Selecciona el algoritmo:</label>
      <select
        id="algorithmSelect"
        value={selectedAlg}
        onChange={(e) => setSelectedAlg(e.target.value)}
        style={inputStyle}
      >
        {Object.keys(algorithmInfo).map((alg) => (
          <option key={alg} value={alg}>
            {algorithmInfo[alg].name}
          </option>
        ))}
      </select>

      <label htmlFor="dataInput" style={labelStyle}>Datos a procesar:</label>
      <textarea
        id="dataInput"
        rows="4"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        style={inputStyle}
        placeholder="Escribe los datos que deseas transformar en hash..."
      />

      <button onClick={generateHash} style={buttonStyle}>Generar Hash</button>

      <div style={resultStyle}>
        <strong>{algorithmInfo[selectedAlg].name} Output:</strong><br />
        {hash || '0'.repeat(64)}
      </div>

      <div style={{ marginTop: '1.5rem', fontSize: '1.1rem', color: '#333', textAlign: 'left' }}>
        <strong>Redes que lo utilizan:</strong><br />
        {algorithmInfo[selectedAlg].networks.map((net, i) => (
          <div key={i}>* {net}</div>
        ))}
      </div>

      <div style={{ marginTop: '1rem', fontSize: '1rem', fontStyle: 'italic', color: '#555', textAlign: 'left' }}>
        {algorithmInfo[selectedAlg].description}
      </div>
    </div>
  );
};

export default BlockchainSimulator;
